import { FlowEventHandlers } from '../../../shared/flow-event';
import {
  LoginByPasswordOneCredentialOptionalFlowEventDefinition,
  LoginByPasswordOneCredentialOptionalFlowEventState,
} from './types';
import { appTimingUtil, monitor } from '../../../platform/monitoring';
import { BusinessPerfMetric } from '../../../platform/monitoring/performance/enums';
import { PageName } from '@shopee-user/url';
import {
  generateTrackingLoginFailedToastAction,
  generateTrackingLoginHelpButtonClick,
  generateTrackingLoginSignupButtonClick,
  generateTrackingLoginView,
  generateTrackingLoginWithPasswordLoginButtonClick,
  generateTrackingLoginBackButtonClick,
  generateTrackingLoginWithSMSButtonClick,
  generateTrackingLoginSuccessAction,
} from '@shopee-user/tracking-login';
import { TrackingDataFieldTypeLoginMethod } from '@shopee-user/tracking-login';
import { TrackingDataFieldTypeOtpScenario } from '@shopee-user/tracking-otp-enter-verification';
import {
  generateTrackingAccountBannedOkButtonClick,
  generateTrackingAccountBannedContactUsButtonClick,
} from '@shopee-user/tracking-otp-enter-verification';
import { generateTrackingAccountBannedImpression } from '@shopee-user/tracking-otp-enter-verification';
import { sendLoginResult } from '@tob/shared/monitoring/business-metric';
import {
  AccType,
  BusinessPage,
  LoginMethod,
  Status,
} from '@tob/shared/monitoring/enums';

export const TRACKER_NAME = 'login-by-password-one-credential-optional';

export const createInitialState = (): LoginByPasswordOneCredentialOptionalFlowEventState => {
  return {
    authKey: '',
  };
};

export function createTracker() {
  const trackPageLoad = ({ pageName }: { pageName: string }) => {
    const softNavigationDuration = appTimingUtil.getDurationFromSoftNavigationStart();
    const isSoftNavigation = softNavigationDuration > 0;

    monitor.sendDuration({
      name: BusinessPerfMetric.PageLoad,
      duration: isSoftNavigation
        ? softNavigationDuration
        : appTimingUtil.getAppAliveDuration(),
      data: {
        soft_nav: isSoftNavigation ? 'true' : 'false',
        page_name: pageName,
        // some of custom metadatas are not allowed to be accessed in Grafana because of database performance concerns
        // so here we use status to indicate the navigation type
        status: isSoftNavigation ? 'soft_nav' : 'hard_nav',
      },
    });
  };

  const trackLoginResult = ({
    status,
    accType,
    loginMethod,
    error,
    apiSource,
  }: {
    status: Status;
    accType: AccType;
    loginMethod: LoginMethod;
    error?: number;
    apiSource?: string;
  }) => {
    sendLoginResult({
      status,
      login_method: loginMethod,
      business_page: BusinessPage.LoginByPasswordOneCredentialOptional,
      acc_type: accType,
      error,
      api_source: apiSource,
    });
  };

  const handlers: FlowEventHandlers<
    LoginByPasswordOneCredentialOptionalFlowEventDefinition,
    LoginByPasswordOneCredentialOptionalFlowEventState
  > = {
    onLoginPageInit: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginView({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
          })
        );
        trackPageLoad({
          pageName: PageName.PageLoginByPassword,
        });
      };
    },
    onLoginLoginFailed: ({
      error,
      errorMsg,
    }: {
      error: number;
      errorMsg: string | undefined;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginFailedToastAction({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
            error,
            errorMsg,
          })
        );
      };
    },
    onLoginSubmitPassword: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginWithPasswordLoginButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
          })
        );
      };
    },
    onLoginBackClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginBackButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
          })
        );
      };
    },
    onLoginHelpClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginHelpButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
          })
        );
      };
    },
    onLoginSMSClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginWithSMSButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
          })
        );
      };
    },
    onLoginSignupClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSignupButtonClick({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
          })
        );
      };
    },
    onLoginAccountBannedInit: () => {
      return ({ tools }) => {
        tools.sendImpressionEvent(
          generateTrackingAccountBannedImpression({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithPassword,
          })
        );
      };
    },
    onLoginAccountBannedOkClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedOkButtonClick({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithPassword,
          })
        );
      };
    },
    onLoginAccountBannedContactUsClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedContactUsButtonClick({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithPassword,
          })
        );
      };
    },
    onLoginSuccess: ({
      accType,
      loginMethod,
    }: {
      accType: AccType;
      loginMethod: LoginMethod;
    }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSuccessAction({
            loginMethod: TrackingDataFieldTypeLoginMethod.Password,
          })
        );

        trackLoginResult({ status: Status.Success, accType, loginMethod });
      };
    },
    onLoginFailed: ({
      accType,
      loginMethod,
      error,
      apiSource,
    }: {
      accType: AccType;
      loginMethod: LoginMethod;
      error: number;
      apiSource: string;
    }) => {
      return () => {
        trackLoginResult({
          status: Status.Failed,
          accType,
          loginMethod,
          error,
          apiSource,
        });
      };
    },
  };

  return {
    name: TRACKER_NAME,
    handlers,
  };
}
